<script setup lang="ts">
/**
 * The API-unreachable banner (SPEC.md §10.4).
 *
 * *"A persistent banner when the API is unreachable; queries retry with exponential backoff."*
 *
 * Persistent, not a toast, and that distinction matters: a toast says "this one thing failed" and
 * goes away. A warehouse operator whose connection has dropped needs to know that **nothing they
 * type will be saved** until it comes back — a message that disappears after four seconds is worse
 * than none, because it lets them keep entering a receipt into a void.
 */
import { AlertTriangle, RefreshCw } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

import { useApiHealth } from '@/shared/composables/useApiHealth'

const { t } = useI18n()
const { isError, isFetching, refetch } = useApiHealth()
</script>

<template>
  <div
    v-if="isError"
    class="bg-danger flex items-center justify-center gap-3 px-4 py-2 text-sm text-white"
    role="alert"
  >
    <AlertTriangle class="size-4 shrink-0" aria-hidden="true" />
    <span>{{ t('errors.apiUnreachable') }}</span>
    <button
      type="button"
      class="flex items-center gap-1.5 rounded-[6px] bg-white/15 px-2 py-0.5 hover:bg-white/25 disabled:opacity-60"
      :disabled="isFetching"
      @click="refetch()"
    >
      <RefreshCw class="size-3.5" :class="isFetching && 'animate-spin'" aria-hidden="true" />
      {{ t('common.retry') }}
    </button>
  </div>
</template>
